import { Component, OnInit, OnDestroy, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ApiServiceService } from '../../services/common/api-service.service';
import { ActivatedRoute, Router } from '@angular/router';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-profile-details',
  templateUrl: './profile-details.component.html',
  styleUrls: ['./profile-details.component.scss']
})
export class ProfileDetailsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() profileId: string | null = null;
  @Input() isEmbedded: boolean = false;
  profileDetails: any;
  base64Image: string = '';
  defaultAvatarUrl = 'assets/images/user/unnamed.jpg';
  currentInterestStatus: string | null = null;
  isReceiver: boolean = false;
  isFavorited: boolean = false;
  loading = true;
  animateFavorite = false;
  similarMatches: any[] = [];
  natchras: any[] = [];
  rasis: any[] = [];
  base64Images: { [key: string]: string } = {};
  selectedMainImage: string | null = null;
  galleryPhotos: string[] = [];
  galleryImagesBase64: { [key: string]: string } = {};
  lastSeen: Date | null = null;
  private presenceInterval: any;

  showReportModal = false;
  reportReason = '';
  reportDescription = '';
  submittingReport = false;
  myProfileId = localStorage.getItem('profileId') || '';

  get isLoggedIn(): boolean {
    return this.isEmbedded || this.isOwnProfile || !!localStorage.getItem('sessionToken') || !!localStorage.getItem('rm_sessionToken') || !!localStorage.getItem('adminToken') || !!localStorage.getItem('rmToken');
  }

  get isOwnProfile(): boolean {
    return localStorage.getItem('profileId') === this.profileId;
  }

  get isGoldOrAbove(): boolean {
    if (this.isEmbedded || this.isOwnProfile || !!localStorage.getItem('rm_sessionToken') || !!localStorage.getItem('rmToken') || !!localStorage.getItem('adminToken')) return true;
    const plan = (this.profileDetails?.userPlan || localStorage.getItem('user_current_plan') || 'FREE').toUpperCase();
    return plan === 'GOLD' || plan === 'PLATINUM' || plan === 'PREMIUM' || plan === 'PREMIUM PLUS';
  }

  get canViewAnnualEarnings(): boolean {
    if (this.isEmbedded || this.isOwnProfile) return true;
    if (!this.isLoggedIn) return false;

    try {
      const storedSubscription = localStorage.getItem('current_subscription');
      if (storedSubscription) {
        const parsed = JSON.parse(storedSubscription);
        if (parsed?.features && typeof parsed.features.viewAnnualEarnings !== 'undefined') {
          return Boolean(parsed.features.viewAnnualEarnings);
        }
      }
    } catch (e) {
      console.warn('[ProfileDetails] Failed to read annual earnings access from subscription cache', e);
    }

    const plan = (localStorage.getItem('user_current_plan') || 'FREE').toUpperCase();
    return plan !== 'FREE';
  }

  get horoscopeAccessGranted(): boolean {
    if (this.isEmbedded || this.isOwnProfile) return true;
    if (!this.isGoldOrAbove) return false;
    return this.currentInterestStatus === 'Accepted';
  }

  getMaskedName(): string {
    if (!this.profileDetails) return '';
    if (this.isLoggedIn) {
      return `${this.profileDetails.basic_info?.first_name || ''} ${this.profileDetails.basic_info?.last_name || ''}`.trim();
    }
    const firstName = this.profileDetails.basic_info?.first_name || '';
    const lastName = this.profileDetails.basic_info?.last_name || '';
    const lastInitial = lastName ? lastName.charAt(0) + '.' : '';
    return `${firstName} ${lastInitial}`.trim() || 'Profile';
  }

  constructor(
    private apiService: ApiServiceService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.profileId = params['id'];
      }
      if (this.profileId) {
        this.getProfileDetail();
        this.startPresencePolling();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['profileId'] && !changes['profileId'].firstChange && this.profileId) {
      this.getProfileDetail();
      this.startPresencePolling();
    }
  }

  ngOnDestroy() {
    if (this.presenceInterval) {
      clearInterval(this.presenceInterval);
    }
  }

  startPresencePolling() {
    if (!this.profileId) return;
    this.fetchPresence();
    this.presenceInterval = setInterval(() => {
      this.fetchPresence();
    }, 30000);
  }

  fetchPresence() {
    if (!this.profileId) return;
    this.apiService.getPresence([this.profileId]).subscribe({
      next: (response) => {
        if (response.status === 'success' && response.data && response.data.length > 0) {
          const p = response.data[0];
          if (p.last_seen) {
            this.lastSeen = new Date(p.last_seen);
          }
        }
      },
      error: (err) => console.error('Error fetching presence:', err)
    });
  }

  isOnline(): boolean {
    if (!this.lastSeen) return false;
    return (Date.now() - this.lastSeen.getTime()) < 120000;
  }

  timeAgo(): string {
    if (!this.lastSeen) return 'Unknown';
    const seconds = Math.floor((Date.now() - this.lastSeen.getTime()) / 1000);
    
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min${minutes !== 1 ? 's' : ''} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hr${hours !== 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'Yesterday';
    return `${days} days ago`;
  }

  getProfileDetail() {
    this.loading = true;
    const sessionToken = localStorage.getItem('sessionToken') || '';
    const userId = localStorage.getItem('profileId') || '';

    if (!this.profileId) {
      console.error('Profile ID not found.');
      this.loading = false;
      return;
    }

    // 1. Fetch reciprocal connection status & favorite status
    if (this.isLoggedIn && userId) {
      this.apiService.getConnectionStatus(sessionToken, userId, this.profileId).subscribe(
        (resp) => {
          if (resp && resp.status === 'success' && resp.data) {
            this.currentInterestStatus = resp.data.interest_status;
            this.isReceiver = resp.data.selected_profile_id === userId;
          } else {
            this.currentInterestStatus = null;
            this.isReceiver = false;
          }
        },
        (error) => console.error('Error fetching connection status:', error)
      );

      this.apiService.getFavorites(sessionToken, userId).subscribe({
        next: (resp) => {
          if (resp && resp.status === 'success' && Array.isArray(resp.data)) {
            this.isFavorited = resp.data.some((fav: any) => {
              const favId = typeof fav.profile === 'object' && fav.profile ? fav.profile._id : fav.profile;
              return favId === this.profileId;
            });
          }
        },
        error: (err) => console.error('Error checking favorite status:', err)
      });
    }

    // 2. Fetch full profile details
    const viewerId = (this.isEmbedded || this.isOwnProfile) ? this.profileId : (userId || '000000000000000000000000');
    this.apiService.getProfileDetails(sessionToken, this.profileId, viewerId).subscribe(
      (response) => {
        if (response && response.data) {
          this.profileDetails = response.data;
          
          if (this.isEmbedded || this.isOwnProfile) {
            this.profileDetails.isContactLocked = false;
            this.profileDetails.isGalleryLocked = false;
            this.profileDetails.isChatLocked = false;
            this.profileDetails.upgradeRequiredMessage = '';
            this.profileDetails.blurPhotos = false;
          }
          
          // Load profile image
          const basic = this.profileDetails.basic_info;
          const personal = this.profileDetails.personal_info;
          this.base64Image = '';
          this.galleryPhotos = [];
          this.galleryImagesBase64 = {};

          if (basic && personal) {
            if (personal.avatar_Id) {
              const keycloakId = basic.keycloak_id || basic.ory_identity_id || this.profileId;
              if (keycloakId) {
                const imgPath = `${keycloakId}/gallery/avatar/${personal.avatar_Id}`;
                this.loadImage(imgPath);
              }
            }

            this.galleryPhotos = personal.gallery_photos || [];
            const keycloakId = basic.keycloak_id || basic.ory_identity_id || this.profileId;
            if (keycloakId) {
              this.galleryPhotos.forEach((photoName: string) => {
                const p = `${keycloakId}/gallery/photos/${photoName}`;
                this.apiService.getImageBase64(p).subscribe(res => {
                  if (res && res.base64Image) {
                    this.galleryImagesBase64[photoName] = res.base64Image;
                  }
                });
              });
            }
          }
        }
        this.loading = false;
      },
      (error) => {
        console.error('Error fetching profile details:', error);
        this.loading = false;
        if (this.apiService.handleQuotaError(error)) {
          if (window.history.length > 1) {
            window.history.back();
          } else {
            this.router.navigate(['/profiles']);
          }
          return;
        }
      }
    );
    
    this.apiService.getNatchra().subscribe(res => {
      if (res && res.status === 'success') this.natchras = res.data;
    });
    this.apiService.getRasi().subscribe(res => {
      if (res && res.status === 'success') this.rasis = res.data;
    });

    this.fetchSimilarProfiles();
  }

  loadImage(imagePath: string) {
    this.apiService.getImageBase64(imagePath).subscribe(
      (data) => {
        if (data && data.base64Image) {
          this.base64Image = data.base64Image;
        }
      },
      (error) => {
        console.error('Error fetching base64 image:', error);
      }
    );
  }

  loadSimilarImage(imagePath: string, profileId: string) {
    this.apiService.getImageBase64(imagePath).subscribe(
      (data) => {
        if (data && data.base64Image) {
          this.base64Images[profileId] = data.base64Image;
        }
      },
      (error) => console.error('Error fetching image for similar profile', error)
    );
  }

  fetchSimilarProfiles() {
    const sessionToken = localStorage.getItem('sessionToken');
    const profileId = localStorage.getItem('profileId');
    if (!sessionToken || !profileId) return;

    // Call the existing preference filter logic to populate similar profiles
    this.apiService.getPreferenceFilter(sessionToken, profileId).subscribe(
      (response) => {
        if (response && response.status === 'success' && Array.isArray(response.data)) {
          // Filter out the currently viewed profile from the similar list
          this.similarMatches = response.data.filter((p: any) => p._id !== this.profileId).slice(0, 10);
          
          this.similarMatches.forEach((profile: any) => {
            const idKey = profile.basic_info?.keycloak_id || profile.basic_info?.ory_identity_id;
            if (profile.personal_info?.avatar_Id && idKey) {
              const imgUrl = `${idKey}/gallery/avatar/${profile.personal_info.avatar_Id}`;
              this.loadSimilarImage(imgUrl, profile._id);
            }
          });
        }
      },
      (error) => console.error('Error fetching similar profiles', error)
    );
  }

  getAge(dateOfBirth: string): number | string {
    if (!dateOfBirth) return 'N/A';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    if (isNaN(birthDate.getTime())) return 'N/A';

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }

  getDisplayValue(value: any, fallback: string = 'Not specified'): string {
    if (value === null || value === undefined || value === '') return fallback;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      const text = String(value).trim();
      if (/^[a-f\d]{24}$/i.test(text)) return fallback;
      return text && text !== '[object Object]' ? text : fallback;
    }
    if (Array.isArray(value)) {
      const values = value.map(item => this.getDisplayValue(item, '')).filter(Boolean);
      return values.length ? values.join(', ') : fallback;
    }
    if (typeof value === 'object') {
      // All actual DB populated field names in priority order
      const candidateKeys = [
        'city_nme', 'state_nme', 'country_nme', 'name_0', 'name_1', 'name_2',
        'religion_nme', 'caste_nme', 'subcaste_nme',
        'occupation_nme', 'education_nme', 'employment_type_nme',
        'language_nme', 'income_nme', 'gothram_nme',
        'natchr_nme', 'natchr_name', 'natchra_nme', 'rasi_nme', 'rasi_name', 'highereducation_nme', 'citizenship_nme',
        'name', 'label', 'title', 'value'
      ];
      for (const key of candidateKeys) {
        if (value[key]) return this.getDisplayValue(value[key], fallback);
      }
    }
    return fallback;
  }

  getLocationLine(): string {
    const location = this.profileDetails?.residing_location || {};
    const city = this.getDisplayValue(location.city_id, '');
    const state = this.getDisplayValue(location.state_id, '');
    const country = this.getDisplayValue(location.country_id, '');
    const parts = [city, state || country].filter(Boolean);
    return parts.length ? parts.join(', ') : 'Not specified';
  }

  getProfessionLine(): string {
    const professional = this.profileDetails?.professional_info || {};
    const occ = professional.occupation_id;
    const emp = professional.employmentype_id;
    const occupation = typeof occ === 'object' && occ ? (occ.occupation_nme || '') : '';
    const employment = typeof emp === 'object' && emp ? (emp.employment_type_nme || '') : '';
    const parts = [occupation, employment].filter(Boolean);
    return parts.length ? parts.join(' · ') : 'Not specified';
  }

  getStarRaasiLine(): string {
    const religion = this.profileDetails?.religion_info || {};
    let starName = '';
    let raasiName = '';

    // First try the populated object if backend fixes populate
    starName = this.getDisplayValue(religion.natchr_id, '');
    raasiName = this.getDisplayValue(religion.rasi_id, '');

    // Fallback: manually look up in fetched arrays if backend populate fails
    if (!starName && typeof religion.natchr_id === 'string' && this.natchras.length) {
      const match = this.natchras.find(s => s._id === religion.natchr_id);
      if (match) starName = match.natchr_nme || match.natchr_name || '';
    }
    if (!raasiName && typeof religion.rasi_id === 'string' && this.rasis.length) {
      const match = this.rasis.find(r => r._id === religion.rasi_id);
      if (match) raasiName = match.rasi_nme || match.rasi_name || '';
    }

    const parts = [starName, raasiName].filter(Boolean);
    return parts.length ? parts.join(' / ') : '--';
  }

  getAddressLine(): string {
    const location = this.profileDetails?.residing_location || {};
    const address1 = this.getDisplayValue(location.address1, '');
    const address2 = this.getDisplayValue(location.address2, '');
    const cityLine = this.getLocationLine();
    const parts = [address1, address2, cityLine].filter(Boolean);
    return parts.length ? parts.join(', ') : '--';
  }

  sendInterest() {
    if (!this.profileId) return;
    const sessionToken = localStorage.getItem('sessionToken');
    const ownerId = localStorage.getItem('profileId');
    const interestStatus = 'Pending';

    if (!sessionToken || !ownerId) {
      this.router.navigate(['/login']);
      return;
    }

    this.apiService.setConnectionStatus(sessionToken, ownerId, this.profileId, interestStatus).subscribe(
      (response) => {
        Swal.fire({
          title: 'Interest Sent!',
          text: `Connection request sent to ${this.profileDetails?.basic_info?.first_name || 'user'}.`,
          icon: 'success',
          confirmButtonColor: '#800020'
        });
        this.currentInterestStatus = 'Pending';
        this.isReceiver = false;
      },
      (error) => {
        if (this.apiService.handleQuotaError(error)) return;
        Swal.fire({
          title: 'Error',
          text: 'An error occurred while sending interest request.',
          icon: 'error',
          confirmButtonColor: '#800020'
        });
        console.error('Error sending interest:', error);
      }
    );
  }

  respondToInterest(status: 'Accepted' | 'Denied') {
    const sessionToken = localStorage.getItem('sessionToken');
    const loggedInUserId = localStorage.getItem('profileId'); // Receiver
    const senderProfileId = this.profileId; // Sender

    if (!sessionToken || !loggedInUserId || !senderProfileId) return;

    this.apiService.setConnectionStatus(sessionToken, senderProfileId, loggedInUserId, status).subscribe(
      (response) => {
        Swal.fire({
          title: 'Success!',
          text: `You have ${status === 'Accepted' ? 'accepted' : 'declined'} the connection request.`,
          icon: 'success',
          confirmButtonColor: '#800020'
        });
        this.currentInterestStatus = status;
      },
      (error) => {
        Swal.fire({
          title: 'Error',
          text: 'Failed to update interest request response.',
          icon: 'error',
          confirmButtonColor: '#800020'
        });
        console.error('Error responding to interest:', error);
      }
    );
  }

  navigateToSocial(platform: string) {
    if (!this.profileDetails?.social_media_info) return;
    let url = '';
    switch (platform) {
      case 'facebook':
        url = this.profileDetails.social_media_info.facebook_id;
        break;
      case 'twitter':
        url = this.profileDetails.social_media_info.twitter_id;
        break;
      case 'linkedin':
        url = this.profileDetails.social_media_info.linkedin_id;
        break;
      case 'instagram':
        url = this.profileDetails.social_media_info.instagram_id;
        break;
    }
    if (url && url !== '--') {
      window.open(url, '_blank');
    }
  }

  addToFavorites() {
    if (!this.profileId) return;
    const sessionToken = localStorage.getItem('sessionToken');
    const ownerId = localStorage.getItem('profileId');
    if (!sessionToken || !ownerId) {
      this.router.navigate(['/login']);
      return;
    }

    if (this.isFavorited) {
      this.apiService.deleteFavorite(sessionToken, this.profileId, ownerId).subscribe({
        next: () => {
          this.isFavorited = false;
          this.apiService.favoritesChanged.next({ profileId: this.profileId || '', action: 'remove' });
          Swal.fire({
            title: 'Removed from Favorites',
            text: `${this.getMaskedName()}'s profile has been removed from your favorites.`,
            icon: 'info',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 2000
          });
        },
        error: (err) => console.error('Error removing favorite:', err)
      });
    } else {
      this.apiService.addToFavorites(
        sessionToken,
        this.profileId,
        ownerId,
        this.profileDetails?.basic_info || {},
        this.profileDetails?.religion_info || {}
      ).subscribe({
        next: () => {
          this.isFavorited = true;
          // emit event so favorites lists can update
          this.apiService.favoritesChanged.next({ profileId: this.profileId || '', action: 'add' });
          // simple icon animation
          this.animateFavorite = true;
          setTimeout(() => this.animateFavorite = false, 650);
          Swal.fire({
            title: 'Added to Favorites!',
            text: `${this.getMaskedName()}'s profile has been added to your favorites.`,
            icon: 'success',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 2000
          });
        },
        error: (err) => {
          if (this.apiService.handleQuotaError(err)) return;
          console.error('Error saving favorite:', err);
          Swal.fire('Error', 'Could not save profile to favorites.', 'error');
        }
      });
    }
  }

  shareProfile() {
    Swal.fire({
      title: 'Share Profile',
      text: 'Profile link copied to clipboard!',
      icon: 'info',
      confirmButtonColor: '#800020'
    });
  }

  downloadBiodata() {
    if (!this.profileId) return;
    if (!this.isOwnProfile && !this.isGoldOrAbove) {
      this.apiService.handleQuotaError({
        error: {
          code: 'LIMIT_REACHED',
          message: 'Downloading candidate Horoscope & Biodata forms is unlocked exclusively on Gold Membership.'
        }
      });
      return;
    }
    if (!this.isOwnProfile && this.currentInterestStatus !== 'Accepted') {
      Swal.fire('Horoscope Locked', 'Horoscope and Biodata downloads are available only when mutual interest request is accepted by both members.', 'info');
      return;
    }
    const userId = localStorage.getItem('profileId') || '';
    this.apiService.downloadBiodataPdf(this.profileId, userId).subscribe(
      (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Biodata_${this.profileDetails?.basic_info?.first_name || 'Profile'}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      (error) => {
        console.error('Download error:', error);
        Swal.fire({
          icon: 'error',
          title: 'Download Failed',
          text: 'You may not have permission to download this file.'
        });
      }
    );
  }

  viewBiodata() {
    if (!this.profileId) return;
    if (!this.isOwnProfile && !this.isGoldOrAbove) {
      this.apiService.handleQuotaError({
        error: {
          code: 'LIMIT_REACHED',
          message: 'Viewing candidate Horoscope & Biodata forms is unlocked exclusively on Gold Membership.'
        }
      });
      return;
    }
    if (!this.isOwnProfile && this.currentInterestStatus !== 'Accepted') {
      Swal.fire('Horoscope Locked', 'Horoscope and Biodata viewing is available only when mutual interest request is accepted by both members.', 'info');
      return;
    }
    const userId = localStorage.getItem('profileId') || '';
    this.apiService.downloadBiodataPdf(this.profileId, userId).subscribe(
      (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 60000); // Revoke after a minute
      },
      (error) => {
        console.error('View error:', error);
        Swal.fire({
          icon: 'error',
          title: 'View Failed',
          text: 'You may not have permission to view this file.'
        });
      }
    );
  }

  onGalleryThumbnailClick(index: number, photoName?: string) {
    // Always allow selecting thumbnails. If a gallery image is selected while
    // `blurPhotos` is true, the main preview will show a blurred locked view.
    if (index === 0) {
      this.selectedMainImage = null; // revert to main DP
      return;
    }
    if (photoName && this.galleryImagesBase64[photoName]) {
      this.selectedMainImage = this.galleryImagesBase64[photoName];
    }
  }

  onLockedGalleryClick() {
    this.apiService.handleQuotaError({
      error: {
        code: 'LIMIT_REACHED',
        message: 'Upgrade your plan to unlock clear gallery media and view full photos.'
      }
    });
  }

  onLockedChatClick() {
    this.apiService.handleQuotaError({
      error: {
        code: 'UPGRADE_REQUIRED',
        message: 'Chat access is locked when both members are on the Free plan. Upgrade to enjoy more benefits and chat instantly!'
      }
    });
  }

  openReportModal(): void {
    this.showReportModal = true;
    this.reportReason = '';
    this.reportDescription = '';
  }

  closeReportModal(): void {
    this.showReportModal = false;
  }

  submitReport(): void {
    if (!this.profileId) return;
    if (!this.reportReason || !this.reportDescription || this.reportDescription.trim().length < 10) {
      Swal.fire({
        title: 'Validation Need',
        text: 'Please select a reason and provide a description of at least 10 characters.',
        icon: 'warning',
        confirmButtonColor: '#b01e3e',
        didOpen: () => {
          const container = document.querySelector('.swal2-container') as HTMLElement;
          if (container) container.style.zIndex = '99999';
        }
      });
      return;
    }

    this.submittingReport = true;
    const payload = {
      reason: this.reportReason,
      description: this.reportDescription,
      reporterProfileId: this.myProfileId
    };

    this.apiService.reportProfile(this.profileId, payload).subscribe({
      next: (response: any) => {
        this.submittingReport = false;
        this.closeReportModal();
        if (response.status === 'success') {
          Swal.fire({
            title: 'Report Submitted',
            text: 'Thank you for keeping our community safe. Our admin team will review this report shortly.',
            icon: 'success',
            confirmButtonColor: '#b01e3e',
            didOpen: () => {
              const container = document.querySelector('.swal2-container') as HTMLElement;
              if (container) container.style.zIndex = '99999';
            }
          });
        } else {
          Swal.fire({
            title: 'Report Failed',
            text: response.message || 'Failed to submit report. Please try again.',
            icon: 'error',
            confirmButtonColor: '#b01e3e',
            didOpen: () => {
              const container = document.querySelector('.swal2-container') as HTMLElement;
              if (container) container.style.zIndex = '99999';
            }
          });
        }
      },
      error: (err: any) => {
        this.submittingReport = false;
        Swal.fire({
          title: 'Error',
          text: err?.error?.message || 'Failed to submit report. Please try again later.',
          icon: 'error',
          confirmButtonColor: '#b01e3e',
          didOpen: () => {
            const container = document.querySelector('.swal2-container') as HTMLElement;
            if (container) container.style.zIndex = '99999';
          }
        });
      }
    });
  }
}
